/**
 * Scene list edits for the reel screen. Pages stay the scenes.
 * No Fabric and no Photo Studio.
 */

import type { ProjectPage } from '@canvas/shared-types';
import { VIDEO_MIN_SCENE_MS } from '@canvas/shared-types';
import { createBlankPage, duplicatePage } from '@/lib/projectPages';
import { patchReelClip } from './reelClip';
import { clipRefsInCanvasData } from '@/lib/video/videoClip';

/** Append a blank scene. */
export function addReelScene(
  pages: ProjectPage[],
  width: number,
  height: number
): ProjectPage[] {
  const page = createBlankPage(width, height, `Scene ${pages.length + 1}`, pages.length + 1);
  return [...pages, page];
}

/** Insert a copy after the active scene. */
export function duplicateReelScene(
  pages: ProjectPage[],
  activePageId: string
): { pages: ProjectPage[]; activePageId: string } | null {
  const idx = pages.findIndex((page) => page.pageId === activePageId);
  if (idx < 0) return null;
  const copy = duplicatePage(pages[idx]);
  const next = [...pages.slice(0, idx + 1), copy, ...pages.slice(idx + 1)];
  return { pages: next, activePageId: copy.pageId };
}

/** Drop the active scene. The last remaining scene stays. */
export function deleteReelScene(
  pages: ProjectPage[],
  activePageId: string
): { pages: ProjectPage[]; activePageId: string } | null {
  if (pages.length <= 1) return null;
  const idx = pages.findIndex((page) => page.pageId === activePageId);
  if (idx < 0) return null;
  const next = pages.filter((page) => page.pageId !== activePageId);
  const fallback = next[Math.max(0, idx - 1)] || next[0];
  return { pages: next, activePageId: fallback.pageId };
}

/** Rename one scene. The artwork on the page is left as it is. */
export function renameReelScene(
  pages: ProjectPage[],
  pageId: string,
  name: string
): ProjectPage[] {
  return pages.map((page) => (page.pageId === pageId ? { ...page, name } : page));
}

export interface ReelSplit {
  pages: ProjectPage[];
  leftId: string;
  rightId: string;
  leftMs: number;
  rightMs: number;
}

/**
 * Split one scene at a scene-local time, the way a timeline blade does.
 * Both sides keep the caption. A clip is trimmed at the same source frame.
 * Returns null when either side would be shorter than the minimum scene.
 */
export function splitReelScene(
  pages: ProjectPage[],
  pageId: string,
  localMs: number,
  sceneMs: number
): ReelSplit | null {
  const idx = pages.findIndex((page) => page.pageId === pageId);
  if (idx < 0) return null;
  const leftMs = Math.round(localMs);
  const rightMs = Math.round(sceneMs) - leftMs;
  if (leftMs < VIDEO_MIN_SCENE_MS || rightMs < VIDEO_MIN_SCENE_MS) return null;
  const page = pages[idx];
  let left = page;
  let right: ProjectPage = { ...duplicatePage(page), name: page.name };
  const clip = clipRefsInCanvasData(page.canvasData)[0];
  if (clip) {
    const at = Math.round(clip.trimStartMs + leftMs * (clip.rate || 1));
    left = patchReelClip(page, { trimEndMs: at });
    right = patchReelClip(right, { trimStartMs: at });
  }
  return {
    pages: [...pages.slice(0, idx), left, right, ...pages.slice(idx + 1)],
    leftId: left.pageId,
    rightId: right.pageId,
    leftMs,
    rightMs,
  };
}
